import { Injectable, computed, inject, signal } from "@angular/core";
// Deep import to keep the simulation-core barrel (and its CastOffProApp
// re-export) out of the initial chunk.
import { BOAT_HOOK_REACH_M } from "../../../simulation-core/sim/MooringPickupService";
import { AppStateStore } from "./app-state.store";
import { SimService } from "./sim.service";

/**
 * Crew-command system: orchestrates manual deck operations (mooring
 * lines, fenders, anchor) with realistic random execution delays so
 * each command takes 2–5 s to complete, matching the time a real crew
 * member needs to walk the deck and handle a line / fender / windlass.
 *
 * The store is purely a UI/orchestration layer — actual physics hooks
 * (e.g. setting `state.mooringTargets[cleat]` once a line reaches
 * `made_fast`) live in components that consume the store and forward
 * to {@link SimService}. This keeps the timing/state-machine logic
 * isolated from the simulation-core API surface.
 *
 * Translations are kept in Polish (UI language) to match the rest of
 * the app: stowed=schowana, prepared=przygotowana,
 * thrown=rzucona, taken_in=wybrana, made_fast=obłożona.
 */
export type LineState =
  | "stowed"
  | "prepared"
  | "thrown"
  | "picked_up"
  | "taken_in"
  | "made_fast";

export type AnchorState = "up" | "dropping" | "down" | "weighing";

/**
 * Shore party — whether at least one crew member is on the dock to
 * receive thrown lines and tie them to bollards. Required before any
 * line can be made fast on the dock side. Pickup-moorings do NOT need
 * shore party (handled with a boat hook from the deck).
 */
export type ShorePartyState =
  | "aboard"
  | "going_ashore"
  | "ashore"
  | "returning";

export const SHORE_LABELS: Record<ShorePartyState, string> = {
  aboard: "na pokładzie",
  going_ashore: "schodzi na brzeg",
  ashore: "na brzegu",
  returning: "wraca na pokład",
};

export interface ShorePartyEntry {
  state: ShorePartyState;
  busy: boolean;
}

/** Cleats present on the player boat, in display order. */
export interface CleatSpec {
  /** Internal key; matches `mooringTargets` in `AppState`. */
  readonly key: string;
  /** UI label (Polish). */
  readonly label: string;
}

export const CREW_CLEATS: readonly CleatSpec[] = [
  { key: "bow", label: "dziobowa" },
  { key: "stern_port", label: "rufowa LB" },
  { key: "stern_starboard", label: "rufowa PB" },
  { key: "midship_port", label: "szpringa LB" },
  { key: "midship_starboard", label: "szpringa PB" },
];

/** Fenders along both sides + ends. */
export interface FenderSpec {
  readonly key: string;
  readonly label: string;
}

export const CREW_FENDERS: readonly FenderSpec[] = [
  { key: "bow_port", label: "dziób LB" },
  { key: "bow_starboard", label: "dziób PB" },
  { key: "midship_port", label: "śród. LB" },
  { key: "midship_starboard", label: "śród. PB" },
  { key: "stern_port", label: "rufa LB" },
  { key: "stern_starboard", label: "rufa PB" },
];

export interface LineEntry {
  state: LineState;
  /** Anchor id this line is rigged to, or null. Set automatically on
   *  the first `prepared`→`thrown` transition; cleared on stow. */
  target: string | null;
  /**
   * Kind of the current target anchor. `'buoy'` lines (free-floating
   * mooring picked up with a boat hook) can be made fast directly
   * from the deck — no shore party required. `'bollard'` lines need
   * crew ashore to receive and tie them.
   */
  targetKind: "buoy" | "bollard" | null;
  /** Rope rest length when made fast. */
  restLength: number;
  /** True while a transition is in progress. */
  busy: boolean;
}

export interface FenderEntry {
  deployed: boolean;
  busy: boolean;
}

export interface AnchorEntry {
  state: AnchorState;
  /** Length of chain currently paid out, metres. */
  chainLength: number;
  /** Estimated local depth used for scope calculation, metres. */
  estimatedDepth: number;
  busy: boolean;
}

const LINE_FORWARD: Record<LineState, LineState | null> = {
  stowed: "prepared",
  prepared: "thrown",
  thrown: "taken_in",
  picked_up: "taken_in",
  taken_in: "made_fast",
  made_fast: null,
};

const LINE_BACKWARD: Record<LineState, LineState | null> = {
  stowed: null,
  prepared: "stowed",
  thrown: "prepared",
  picked_up: "stowed",
  taken_in: "thrown",
  made_fast: "taken_in",
};

export const LINE_LABELS: Record<LineState, string> = {
  stowed: "schowana",
  prepared: "przygotowana",
  thrown: "rzucona",
  picked_up: "mooring podjęty",
  taken_in: "wybrana",
  made_fast: "obłożona",
};

/**
 * Crew commands that can be issued for a single mooring line. Each
 * command has its own set of valid source states — the drawer
 * disables buttons whose source set does not include the current
 * line state. Order in the array is the order shown in the UI.
 */
export interface LineCommandSpec {
  readonly id: string;
  readonly label: string;
  readonly from: readonly LineState[];
  readonly to: LineState;
}

export const LINE_COMMANDS: readonly LineCommandSpec[] = [
  { id: "prepare", label: "Przygotuj", from: ["stowed"], to: "prepared" },
  { id: "throw", label: "Rzuć", from: ["prepared"], to: "thrown" },
  /**
   * Pass an already-prepared line through the eye of a free-floating
   * mooring buoy using a boat hook (bosak), bring it back to the same
   * cleat and secure. Goes directly from `prepared` to `made_fast` —
   * no shore party needed. Only available on bow / stern cleats and
   * only while a buoy is within boat-hook reach (see
   * {@link BOAT_HOOK_REACH_M}); the action is cancelled if the boat
   * drifts out of reach mid-handling.
   */
  {
    id: "loop_buoy",
    label: "Przełóż przez bojkę",
    from: ["prepared"],
    to: "made_fast",
  },
  {
    id: "take_in",
    label: "Wybierz",
    from: ["thrown", "picked_up"],
    to: "taken_in",
  },
  { id: "make_fast", label: "Obłóż", from: ["taken_in"], to: "made_fast" },
  { id: "release", label: "Luzuj", from: ["made_fast"], to: "taken_in" },
  {
    id: "stow",
    label: "Zwiń",
    from: ["prepared", "thrown", "picked_up", "taken_in"],
    to: "stowed",
  },
];

export const ANCHOR_LABELS: Record<AnchorState, string> = {
  up: "wybrana",
  dropping: "rzucanie",
  down: "rzucona",
  weighing: "wybieranie",
};

/**
 * Mast stepping procedure for foldable-mast yachts (tabernacle, e.g.
 * Antila 27). The crew walks the deck, slackens the standing rigging,
 * pulls the forestay pin ("zawleczka" na dziobie) and lowers the
 * spar aft onto the gallows; reversing the steps puts it back up.
 *
 * States are gated linearly — you cannot jump from `stepped` to
 * `down` without going through halyards-slack and forestay-free.
 * The order matches the real-world checklist a single skipper would
 * follow before launching on a trailer-sailer.
 */
export type MastState =
  | "stepped"
  | "halyards_slack"
  | "forestay_free"
  | "boom_off"
  | "lowering"
  | "down"
  | "raising";

export const MAST_LABELS: Record<MastState, string> = {
  stepped: "postawiony",
  halyards_slack: "fały poluzowane",
  forestay_free: "sztag odpięty",
  boom_off: "bom zdjęty",
  lowering: "kładzenie",
  down: "położony",
  raising: "stawianie",
};

export interface MastEntry {
  state: MastState;
  busy: boolean;
}

export interface MastCommandSpec {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly from: readonly MastState[];
  readonly to: MastState;
  /** Transient state shown while the action is in progress. */
  readonly during?: MastState;
}

/**
 * Reversible mast-stepping checklist. Ordered the way the crew would
 * walk through it: top half lowers, bottom half raises. Each command
 * is gated by the boat's current mast state and disabled in the UI
 * otherwise. Lowering / raising the spar itself shows a transient
 * `lowering` / `raising` state for the duration of the action.
 */
export const MAST_COMMANDS: readonly MastCommandSpec[] = [
  {
    id: "slacken_halyards",
    label: "Poluzuj fały",
    hint: "Luzowanie fałów masztu (grota i foka) na knagach.",
    from: ["stepped"],
    to: "halyards_slack",
  },
  {
    id: "free_forestay",
    label: "Wyjmij zawleczkę sztagu",
    hint: "Wyjęcie zawleczki sztagu na dziobie i odpięcie sztagu.",
    from: ["halyards_slack"],
    to: "forestay_free",
  },
  {
    id: "detach_boom",
    label: "Zdejmij bom",
    hint: "Odpięcie bomu od goosenecku przed położeniem masztu.",
    from: ["forestay_free"],
    to: "boom_off",
  },
  {
    id: "lower_mast",
    label: "Połóż maszt na rufę",
    hint: "Kładzenie masztu na podporze rufowej.",
    from: ["boom_off"],
    to: "down",
    during: "lowering",
  },
  {
    id: "raise_mast",
    label: "Postaw maszt",
    hint: "Stawianie masztu w gnieździe (tabernakulum).",
    from: ["down"],
    to: "boom_off",
    during: "raising",
  },
  {
    id: "attach_boom",
    label: "Załóż bom",
    hint: "Wpięcie bomu w gooseneck po postawieniu masztu.",
    from: ["boom_off"],
    to: "forestay_free",
  },
  {
    id: "secure_forestay",
    label: "Załóż zawleczkę sztagu",
    hint: "Wpięcie sztagu i zabezpieczenie zawleczką.",
    from: ["forestay_free"],
    to: "halyards_slack",
  },
  {
    id: "tension_halyards",
    label: "Napnij fały",
    hint: "Napięcie i obłożenie fałów na knagach.",
    from: ["halyards_slack"],
    to: "stepped",
  },
];

/** Random execution time in milliseconds for any crew action. */
function rollDelay(): number {
  return 2000 + Math.random() * 3000;
}

@Injectable({ providedIn: "root" })
export class CrewCommandsStore {
  private readonly sim = inject(SimService);
  private readonly appState = inject(AppStateStore);

  constructor() {
    if (import.meta.env.DEV) {
      (window as unknown as { __crew?: unknown }).__crew = this;
    }
  }

  /** Cleats that can use a boat hook to pick up a free-floating buoy. */
  private static readonly PICKUP_CLEATS = new Set<string>([
    "bow",
    "stern_port",
    "stern_starboard",
  ]);

  private readonly _lines = signal<Record<string, LineEntry>>(
    Object.fromEntries(
      CREW_CLEATS.map((c) => [
        c.key,
        {
          state: "stowed",
          target: null,
          targetKind: null,
          restLength: 8,
          busy: false,
        },
      ]),
    ),
  );
  private readonly _fenders = signal<Record<string, FenderEntry>>(
    Object.fromEntries(
      CREW_FENDERS.map((f) => [f.key, { deployed: false, busy: false }]),
    ),
  );
  private readonly _anchor = signal<AnchorEntry>({
    state: "up",
    chainLength: 0,
    estimatedDepth: 3,
    busy: false,
  });
  private readonly _shore = signal<ShorePartyEntry>({
    state: "aboard",
    busy: false,
  });
  private readonly _mast = signal<MastEntry>({
    state: "stepped",
    busy: false,
  });

  readonly lines = this._lines.asReadonly();
  readonly fenders = this._fenders.asReadonly();
  readonly anchor = this._anchor.asReadonly();
  readonly shore = this._shore.asReadonly();
  readonly mast = this._mast.asReadonly();

  /** True if the player boat has a tabernacle / foldable mast at all. */
  readonly mastFoldable = computed(() => this.sim.playerHasFoldableMast());

  /** True if the mast is fully laid down (used for badge / windage). */
  readonly mastDown = computed(() => {
    const s = this._mast().state;
    return s === "down" || s === "lowering" || s === "raising";
  });

  /** Number of in-flight crew actions (used to badge buttons). */
  readonly busyCount = computed(() => {
    let n = 0;
    const ls = this._lines();
    for (const k in ls) if (ls[k]!.busy) n += 1;
    const fs = this._fenders();
    for (const k in fs) if (fs[k]!.busy) n += 1;
    if (this._anchor().busy) n += 1;
    if (this._shore().busy) n += 1;
    return n;
  });

  /** Number of lines made fast — useful for shell badge. */
  readonly madeFastCount = computed(() => {
    const ls = this._lines();
    let n = 0;
    for (const k in ls) if (ls[k]!.state === "made_fast") n += 1;
    return n;
  });

  /** Number of fenders deployed. */
  readonly fendersOutCount = computed(() => {
    const fs = this._fenders();
    let n = 0;
    for (const k in fs) if (fs[k]!.deployed) n += 1;
    return n;
  });

  /** Whether at least one chain link is in the water. */
  readonly anchorDown = computed(() => {
    const a = this._anchor();
    return a.state === "down" || a.state === "dropping";
  });

  /** Whether the shore party is on the dock and able to handle lines. */
  readonly crewAshore = computed(() => this._shore().state === "ashore");

  /**
   * True iff the boat is currently close enough to land/dock that a
   * single crew member could step ashore (or back on board). We reuse
   * the same proximity probe as the boat-hook pickup service: if any
   * bow/stern fairlead has a bollard in boat-hook reach (~2 m), the
   * gunwale is alongside the dock and a sailor can hop across. The
   * signal re-evaluates every time `sim.pickupCandidates` polls.
   *
   * If the captain motors away after dropping someone off, this flips
   * back to false and `recallCrew()` becomes unavailable until the
   * boat returns within reach — leaving a literal stranded-sailor
   * indicator on the icon rail.
   */
  readonly playerNearShore = computed(() => {
    const cands = this.sim.pickupCandidates();
    for (const c of CrewCommandsStore.PICKUP_CLEATS) {
      const cand = cands[c];
      if (cand && cand.kind === "bollard") return true;
    }
    return false;
  });

  /**
   * True if a sailor is on the dock OR mid-transfer right now.
   * Used by the icon rail to badge the crew icon (1) when at least
   * one crew member is off the boat.
   */
  readonly sailorOffBoat = computed(() => {
    const s = this._shore().state;
    return s !== "aboard";
  });

  /**
   * True if a sailor was put ashore but the boat has since drifted
   * out of step-aboard reach — the most important state to surface
   * because the captain has effectively stranded crew. Drives a
   * ⚠ visual cue on the icon rail.
   */
  readonly sailorStranded = computed(
    () => this._shore().state === "ashore" && !this.playerNearShore(),
  );

  /**
   * Live pickup candidate (closest buoy in boat-hook reach) for the
   * given cleat, or null if nothing is in range / cleat is not pickup
   * eligible. Re-evaluates as the simulation polls anchor positions.
   */
  pickupCandidate(cleat: string) {
    if (!CrewCommandsStore.PICKUP_CLEATS.has(cleat)) return null;
    return this.sim.pickupCandidates()[cleat] ?? null;
  }

  // ---------- Lines ------------------------------------------------------

  advanceLine(cleat: string): void {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return;
    const next = LINE_FORWARD[cur.state];
    if (!next) return;
    this.transitionLine(cleat, next);
  }

  retreatLine(cleat: string): void {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return;
    const prev = LINE_BACKWARD[cur.state];
    if (!prev) return;
    this.transitionLine(cleat, prev);
  }

  /**
   * Execute a named crew command on a line. The command must be
   * applicable to the line's current state (see {@link LINE_COMMANDS}).
   * Returns silently if the command is not applicable or the line is
   * already busy. Note: `make_fast` additionally requires the shore
   * party to be ashore (someone has to tie the line to a bollard) —
   * unless the line is already attached to a free-floating buoy
   * (picked up with a boat hook), in which case it can be made fast
   * directly from the deck.
   */
  commandLine(cleat: string, commandId: string): void {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return;
    const cmd = LINE_COMMANDS.find((c) => c.id === commandId);
    if (!cmd || !cmd.from.includes(cur.state)) return;
    if (
      cmd.id === "make_fast" &&
      this.requiresShoreParty(cur) &&
      !this.crewAshore()
    ) {
      return;
    }
    if (cmd.id === "loop_buoy") {
      this.startLoopBuoy(cleat);
      return;
    }
    this.transitionLine(cleat, cmd.to);
  }

  /** True if the given command can be issued right now for this cleat. */
  canCommandLine(cleat: string, commandId: string): boolean {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return false;
    const cmd = LINE_COMMANDS.find((c) => c.id === commandId);
    if (!cmd || !cmd.from.includes(cur.state)) return false;
    if (
      cmd.id === "make_fast" &&
      this.requiresShoreParty(cur) &&
      !this.crewAshore()
    ) {
      return false;
    }
    if (cmd.id === "loop_buoy") {
      // Only bow / stern can use a boat hook, AND a BUOY must be in
      // reach right now (a bollard candidate doesn't count — that's
      // a different command). Re-evaluates whenever pickupCandidates
      // ticks.
      if (!CrewCommandsStore.PICKUP_CLEATS.has(cleat)) return false;
      const cand = this.sim.pickupCandidates()[cleat];
      if (!cand || cand.kind !== "buoy") return false;
    }
    return true;
  }

  /** Lines attached to bollards need a crew member ashore to handle them. */
  private requiresShoreParty(entry: LineEntry): boolean {
    return entry.targetKind !== "buoy";
  }

  setLineRestLength(cleat: string, length: number): void {
    const cur = this._lines()[cleat];
    if (!cur) return;
    this._lines.update((all) => ({
      ...all,
      [cleat]: { ...cur, restLength: Math.max(1, length) },
    }));
  }

  /**
   * Adjust the rest length of an already made-fast line by `deltaM`
   * metres (negative = shorten / "wybierz"; positive = pay out /
   * "luzuj"). Models the crew hauling on the line for ~1.5–2 s; the
   * boat is pulled toward the anchor (or allowed to drift away)
   * smoothly because the rope is a damped spring whose natural
   * length we change.
   *
   * Bollard lines need someone ashore to handle them; buoy lines
   * are tended from the deck. Returns true on success, false when
   * the line is busy / not made-fast / preconditions unmet.
   */
  adjustLineLength(cleat: string, deltaM: number): boolean {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return false;
    if (cur.state !== "made_fast" || !cur.target) return false;
    if (this.requiresShoreParty(cur) && !this.crewAshore()) return false;
    const next = Math.max(1, cur.restLength + deltaM);
    if (Math.abs(next - cur.restLength) < 1e-3) return false;
    this.markLineBusy(cleat, true);
    const delayMs = 1200 + Math.random() * 800;
    setTimeout(() => {
      this._lines.update((all) => {
        const prev = all[cleat];
        if (!prev) return all;
        return { ...all, [cleat]: { ...prev, restLength: next, busy: false } };
      });
      this.syncMooringPhysics(cleat);
    }, delayMs);
    return true;
  }

  /**
   * Reason a line-length adjustment can't be performed right now,
   * for tooltips. Empty string means it's allowed.
   */
  adjustBlockReason(cleat: string, deltaM: number): string {
    const cur = this._lines()[cleat];
    if (!cur) return "Brak takiej cumy";
    if (cur.busy) return "Załoga jest zajęta";
    if (cur.state !== "made_fast" || !cur.target) {
      return "Cuma musi być obłożona";
    }
    if (this.requiresShoreParty(cur) && !this.crewAshore()) {
      return "Wyślij załogę na brzeg";
    }
    if (deltaM < 0 && cur.restLength + deltaM < 1.0) {
      return "Cuma jest już maksymalnie wybrana";
    }
    return "";
  }

  /**
   * Pin the rope to a specific anchor before throwing / making fast.
   * `kind` (buoy vs bollard) drives downstream behaviour: buoy lines
   * can be made fast from the deck (no shore party), bollard lines
   * cannot. Pass `null` for both to clear the target. An optional
   * `restLength` overrides the default 8 m so contextual menus can
   * pre-compute a sensible length from the click distance.
   */
  setLineTarget(
    cleat: string,
    target: string | null,
    kind: "buoy" | "bollard" | null = null,
    restLength?: number,
  ): void {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return;
    this._lines.update((all) => ({
      ...all,
      [cleat]: {
        ...cur,
        target,
        targetKind: target === null ? null : kind,
        restLength:
          restLength !== undefined && restLength > 0
            ? restLength
            : cur.restLength,
      },
    }));
  }

  /**
   * One-shot "moor here" command driven by the in-world contextual
   * menu (tap a poller/buoy → choose a cleat). Bypasses the
   * step-by-step Przygotuj/Rzuć/Wybierz/Obłóż workflow with one
   * combined delay representing the whole sequence so the captain
   * can issue a high-level intent and watch the crew execute it.
   *
   * Pre-flight checks:
   *  - Cleat must exist and not be busy.
   *  - For `'buoy'` targets: the cleat must be pickup-eligible
   *    (bow/stern only) AND that buoy must be in boat-hook reach
   *    right now — otherwise the crew can't loop the line through
   *    the eye.
   *  - For `'bollard'` targets: the shore party must be ashore to
   *    receive the line (somebody has to tie it).
   *
   * Returns true if the command was queued, false if a precondition
   * failed (caller can read `assignmentBlockReason` for a tooltip).
   */
  assignToAnchor(
    cleat: string,
    anchorId: string,
    kind: "buoy" | "bollard",
    restLength: number,
  ): boolean {
    const cur = this._lines()[cleat];
    if (!cur || cur.busy) return false;
    if (kind === "buoy") {
      if (!CrewCommandsStore.PICKUP_CLEATS.has(cleat)) return false;
      const probe = this.sim.probePickupCandidate(cleat);
      if (!probe || probe.anchorId !== anchorId) return false;
    } else {
      if (!this.crewAshore()) return false;
    }
    // Set the target up front so the line knows where it's going
    // (visible in the panel even mid-action).
    this._lines.update((all) => {
      const prev = all[cleat];
      if (!prev) return all;
      return {
        ...all,
        [cleat]: {
          ...prev,
          target: anchorId,
          targetKind: kind,
          restLength,
          busy: true,
        },
      };
    });
    // Combined delay: 3 stages for bollard (przygotuj + rzuć + obłóż),
    // 2 stages for buoy (przygotuj + przełóż przez bojkę). Bow on a
    // high-freeboard hull adds the same penalty as plain loop_buoy.
    const stages = kind === "bollard" ? 3 : 2;
    let delayMs = stages * (2000 + Math.random() * 1500);
    if (kind === "buoy" && cleat === "bow") {
      const fb = this.sim.playerFreeboard();
      delayMs += 1000 + 1500 * Math.max(0, fb);
    }
    setTimeout(() => {
      this._lines.update((all) => {
        const prev = all[cleat];
        if (!prev) return all;
        return {
          ...all,
          [cleat]: {
            ...prev,
            state: "made_fast",
            target: anchorId,
            targetKind: kind,
            restLength,
            busy: false,
          },
        };
      });
      this.syncMooringPhysics(cleat);
    }, delayMs);
    return true;
  }

  /**
   * Tooltip text explaining why `assignToAnchor` would refuse this
   * cleat / anchor combination right now (or empty string if it
   * would succeed).
   */
  assignmentBlockReason(
    cleat: string,
    anchorId: string,
    kind: "buoy" | "bollard",
  ): string {
    const cur = this._lines()[cleat];
    if (!cur) return "Brak takiej cumy";
    if (cur.busy) return "Załoga wykonuje inne polecenie";
    if (kind === "buoy") {
      if (!CrewCommandsStore.PICKUP_CLEATS.has(cleat)) {
        return "Bosakiem przełożysz cumę tylko z dziobu lub rufy";
      }
      const probe = this.sim.probePickupCandidate(cleat);
      if (!probe || probe.anchorId !== anchorId) {
        return "Bojka poza zasięgiem bosaka — podpłyń bliżej";
      }
    } else if (!this.crewAshore()) {
      return "Wyślij załogę na brzeg, żeby założyła cumę";
    }
    return "";
  }

  private transitionLine(cleat: string, next: LineState): void {
    this.markLineBusy(cleat, true);
    setTimeout(() => {
      this._lines.update((all) => {
        const prev = all[cleat];
        if (!prev) return all;
        const target = next === "stowed" ? null : prev.target;
        const targetKind = next === "stowed" ? null : prev.targetKind;
        return {
          ...all,
          [cleat]: { ...prev, state: next, target, targetKind, busy: false },
        };
      });
      this.syncMooringPhysics(cleat);
    }, rollDelay());
  }

  /**
   * Pass an already-prepared line through the eye of the closest
   * in-reach mooring buoy and bring it back to the cleat. Skips the
   * `thrown` / `taken_in` states because the rope never leaves the
   * deck — it's just looped through the buoy with a boat hook.
   *
   * Picking up from the bow on a high-freeboard yacht is harder than
   * from the stern (the crew has to lean out and over a tall topside
   * to spear the eye), so the bow takes ~1.0 + 1.5·freeboard seconds
   * extra. The action polls the buoy's distance every 200 ms while in
   * progress; if the boat drifts further than {@link BOAT_HOOK_REACH_M}
   * away the line is rolled back to `prepared` (still ready to retry).
   */
  private startLoopBuoy(cleat: string): void {
    const cand0 = this.sim.pickupCandidates()[cleat];
    if (!cand0 || cand0.kind !== "buoy") return;
    const targetId = cand0.anchorId;
    const restLength = cand0.restLength;

    // Base 2.5–4 s, plus bow-on-high-freeboard penalty.
    const fb = this.sim.playerFreeboard();
    let delayMs = 2500 + Math.random() * 1500;
    if (cleat === "bow") delayMs += 1000 + 1500 * Math.max(0, fb);

    this.markLineBusy(cleat, true);

    // Watchdog: cancel if buoy drifts out of boat-hook range.
    let cancelled = false;
    const watch = setInterval(() => {
      const probe = this.sim.probePickupCandidate(cleat);
      if (
        !probe ||
        probe.anchorId !== targetId ||
        probe.distance > BOAT_HOOK_REACH_M
      ) {
        cancelled = true;
        clearInterval(watch);
        clearTimeout(finishTimer);
        this._lines.update((all) => {
          const prev = all[cleat];
          if (!prev) return all;
          // Roll back to `prepared` — line is still uncoiled & ready.
          return {
            ...all,
            [cleat]: {
              ...prev,
              state: "prepared",
              target: null,
              targetKind: null,
              busy: false,
            },
          };
        });
      }
    }, 200);

    const finishTimer = setTimeout(() => {
      if (cancelled) return;
      clearInterval(watch);
      this._lines.update((all) => {
        const prev = all[cleat];
        if (!prev) return all;
        return {
          ...all,
          [cleat]: {
            ...prev,
            state: "made_fast",
            target: targetId,
            targetKind: "buoy",
            restLength,
            busy: false,
          },
        };
      });
      this.syncMooringPhysics(cleat);
    }, delayMs);
  }

  /**
   * Push the current line state into the simulation's mooring system.
   * Called after every transition. `made_fast` lines actually pull on
   * the boat; all other states leave the mooring slot detached.
   */
  private syncMooringPhysics(cleat: string): void {
    const entry = this._lines()[cleat];
    if (!entry) return;
    const targets = { ...this.appState.state().mooringTargets };
    const lengths = { ...this.appState.state().mooringRestLengths };
    if (entry.state === "made_fast" && entry.target) {
      targets[cleat] = entry.target;
      lengths[cleat] = entry.restLength;
    } else if (targets[cleat] !== null && targets[cleat] !== undefined) {
      targets[cleat] = null;
    }
    this.appState.patch({
      mooringTargets: targets,
      mooringRestLengths: lengths,
    });
  }

  private markLineBusy(cleat: string, busy: boolean): void {
    this._lines.update((all) => {
      const prev = all[cleat];
      if (!prev) return all;
      return { ...all, [cleat]: { ...prev, busy } };
    });
  }

  // ---------- Fenders ----------------------------------------------------

  /** Aggregate per-side deployment → push to AppState so the renderer can
   *  toggle the GLB fender groups. Also writes per-position deploy flags
   *  consumed by the physics-driven {@link PlayerFenders} pendulum system. */
  private syncFenderVisibilityToAppState(
    fenders: Record<string, FenderEntry>,
  ): void {
    let port = false;
    let starboard = false;
    const perPos: Record<string, boolean> = {};
    for (const f of CREW_FENDERS) {
      const e = fenders[f.key];
      const deployed = !!e?.deployed;
      perPos[f.key] = deployed;
      if (!deployed) continue;
      if (f.key.endsWith("_port")) port = true;
      else if (f.key.endsWith("_starboard")) starboard = true;
    }
    this.appState.patch({
      fendersPortVisible: port,
      fendersStarboardVisible: starboard,
      fenderDeployed: perPos,
    });
  }

  toggleFender(key: string): void {
    const cur = this._fenders()[key];
    if (!cur || cur.busy) return;
    const target = !cur.deployed;
    this._fenders.update((all) => ({
      ...all,
      [key]: { ...cur, busy: true },
    }));
    setTimeout(() => {
      this._fenders.update((all) => {
        const prev = all[key];
        if (!prev) return all;
        const next = { ...all, [key]: { deployed: target, busy: false } };
        this.syncFenderVisibilityToAppState(next);
        return next;
      });
    }, rollDelay());
  }

  deployAllFenders(deployed: boolean): void {
    for (const f of CREW_FENDERS) {
      const cur = this._fenders()[f.key];
      if (!cur || cur.busy || cur.deployed === deployed) continue;
      this.toggleFender(f.key);
    }
  }

  // ---------- Anchor -----------------------------------------------------

  /** Drop the anchor and pay out the requested rode length (metres). */
  dropAnchor(chainLength: number, estimatedDepth = 3): void {
    const cur = this._anchor();
    if (cur.busy || cur.state !== "up") return;
    const depth = Math.max(0.5, estimatedDepth);
    const maxLength = this.sim.playerAnchorEquipment().anchorRodeMaxLength;
    const length = Math.min(Math.max(5, chainLength), maxLength);
    this._anchor.set({ ...cur, state: "dropping", busy: true });
    this.appState.patch({
      anchorState: "dropping",
      anchorChainLength: length,
      anchorEstimatedDepth: depth,
      anchorHoldingState: "setting",
      anchorDeployT: 1,
    });
    setTimeout(() => {
      this._anchor.set({
        state: "down",
        chainLength: length,
        estimatedDepth: depth,
        busy: false,
      });
      this.appState.patch({
        anchorState: "down",
        anchorChainLength: length,
        anchorEstimatedDepth: depth,
        anchorHoldingState: "setting",
        anchorDeployT: 1,
      });
    }, rollDelay());
  }

  /** Weigh anchor — haul it back up into the locker. */
  weighAnchor(): void {
    const cur = this._anchor();
    if (cur.busy || cur.state !== "down") return;
    this._anchor.set({ ...cur, state: "weighing", busy: true });
    this.appState.patch({
      anchorState: "weighing",
      anchorHoldingState: "setting",
      anchorDeployT: 0,
    });
    setTimeout(() => {
      this._anchor.set({
        state: "up",
        chainLength: 0,
        estimatedDepth: cur.estimatedDepth,
        busy: false,
      });
      this.appState.patch({
        anchorState: "up",
        anchorChainLength: 0,
        anchorWorldX: null,
        anchorWorldZ: null,
        anchorHoldingState: "stowed",
        anchorDeployT: 0,
      });
    }, rollDelay());
  }

  // ---------- Shore party ------------------------------------------------

  /**
   * Send the on-deck sailor onto the dock. Requires the boat to be
   * alongside (`playerNearShore()`) — otherwise the request is
   * silently rejected, mirroring the disabled state shown in the UI.
   */
  sendCrewAshore(): void {
    const cur = this._shore();
    if (cur.busy || cur.state !== "aboard") return;
    if (!this.playerNearShore()) return;
    this._shore.set({ state: "going_ashore", busy: true });
    setTimeout(() => {
      this._shore.set({ state: "ashore", busy: false });
    }, rollDelay());
  }

  /**
   * Recall the sailor from the dock. Requires the boat to still be
   * alongside; if the captain has motored away, the sailor is
   * stranded until the boat returns.
   */
  recallCrew(): void {
    const cur = this._shore();
    if (cur.busy || cur.state !== "ashore") return;
    if (!this.playerNearShore()) return;
    this._shore.set({ state: "returning", busy: true });
    setTimeout(() => {
      this._shore.set({ state: "aboard", busy: false });
    }, rollDelay());
  }

  // ---------- Mast (foldable tabernacle) ---------------------------------

  /** True if the named mast command can be issued right now. */
  canCommandMast(commandId: string): boolean {
    if (!this.mastFoldable()) return false;
    const cur = this._mast();
    if (cur.busy) return false;
    const cmd = MAST_COMMANDS.find((c) => c.id === commandId);
    if (!cmd) return false;
    return cmd.from.includes(cur.state);
  }

  /**
   * Execute a mast stepping/unstepping command. Silently no-ops when
   * the boat has no tabernacle, the mast is already mid-action, or
   * the command is not valid for the current state. Lower/raise show
   * a transient `lowering` / `raising` state for the action duration.
   */
  commandMast(commandId: string): void {
    if (!this.mastFoldable()) return;
    const cur = this._mast();
    if (cur.busy) return;
    const cmd = MAST_COMMANDS.find((c) => c.id === commandId);
    if (!cmd || !cmd.from.includes(cur.state)) return;
    const transient = cmd.during ?? cur.state;
    this._mast.set({ state: transient, busy: true });
    if (cmd.id === "lower_mast") {
      this.sim.setPlayerMastAngleTarget(Math.PI / 2);
    } else if (cmd.id === "raise_mast") {
      this.sim.setPlayerMastAngleTarget(0);
    }
    setTimeout(() => {
      if (cmd.id === "detach_boom") {
        this.sim.setPlayerBoomDetached(true);
      } else if (cmd.id === "attach_boom") {
        this.sim.setPlayerBoomDetached(false);
      }
      this._mast.set({ state: cmd.to, busy: false });
    }, rollDelay());
  }
}
